
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { BetaFormValues, betaFormSchema } from './AILeadEngineBetaFormSchema';

const AILeadEngineBetaForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<BetaFormValues>({
    resolver: zodResolver(betaFormSchema)
  });

  const privacyAccepted = watch('privacyAccepted');

  const onSubmit = async (data: BetaFormValues) => {
    try {
      console.log('BETA form submission:', data);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      toast.success('Aanvraag succesvol verzonden!', {
        description: 'We nemen binnen 24 uur contact met je op.'
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    }
  };

  if (isSubmitted) {
    return (
      <section id="beta-form" className="py-20 bg-gradient-to-br from-brand-lightBlue to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg border">
              <div className="bg-green-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Bedankt voor je interesse!
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Je aanvraag is ontvangen. We beoordelen je geschiktheid voor het BETA programma 
                en nemen binnen 24 uur contact met je op.
              </p>
              <div className="bg-brand-blue/5 rounded-lg p-6">
                <p className="text-sm text-gray-600">
                  <strong>Volgende stappen:</strong><br />
                  1. We bekijken je bedrijfsprofiel<br />
                  2. We plannen een kennismakingsgesprek<br />
                  3. Bij geschiktheid krijg je exclusieve toegang tot de AI Lead Engine
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="beta-form" className="py-20 bg-gradient-to-br from-brand-lightBlue to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-brand-red/10 text-brand-red rounded-full text-sm font-medium mb-8">
              <span className="w-2 h-2 bg-brand-red rounded-full mr-2 animate-pulse"></span>
              Beperkte BETA toegang
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Vraag toegang tot de AI Lead Engine
            </h2>
            <p className="text-xl text-gray-700">
              Word één van de eerste bedrijven die profiteren van AI-gedreven leadgeneratie
            </p>
          </div>

          {/* Form Card */}
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-white rounded-t-lg">
              <CardTitle className="text-2xl text-center text-gray-900">
                BETA Aanvraag Formulier
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName">Voornaam *</Label>
                    <Input
                      {...register('firstName')}
                      id="firstName"
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Achternaam *</Label>
                    <Input
                      {...register('lastName')}
                      id="lastName"
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">E-mailadres *</Label>
                  <Input
                    {...register('email')}
                    id="email"
                    type="email"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone">Telefoonnummer *</Label>
                  <Input
                    {...register('phone')}
                    id="phone"
                    type="tel"
                    className={errors.phone ? 'border-red-500' : ''}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                  )}
                </div>

                {/* Company Info */}
                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-gray-900 mb-6">Bedrijfsgegevens</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company">Bedrijfsnaam *</Label>
                      <Input
                        {...register('company')}
                        id="company"
                        className={errors.company ? 'border-red-500' : ''}
                      />
                      {errors.company && (
                        <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="industry">Sector *</Label>
                      <Input
                        {...register('industry')}
                        id="industry"
                        placeholder="bv. IT, Consultancy, Manufacturing"
                        className={errors.industry ? 'border-red-500' : ''}
                      />
                      {errors.industry && (
                        <p className="text-red-500 text-sm mt-1">{errors.industry.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="companySize">Aantal medewerkers *</Label>
                      <Input
                        {...register('companySize')}
                        id="companySize"
                        placeholder="bv. 10-50"
                        className={errors.companySize ? 'border-red-500' : ''}
                      />
                      {errors.companySize && (
                        <p className="text-red-500 text-sm mt-1">{errors.companySize.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="currentLeadGenMethod">Huidige leadgeneratie methode</Label>
                      <Input
                        {...register('currentLeadGenMethod')}
                        id="currentLeadGenMethod"
                        placeholder="bv. Cold calling, LinkedIn, events"
                      />
                    </div>
                  </div>
                </div>

                {/* Motivation */}
                <div className="pt-6 border-t">
                  <Label htmlFor="motivation">Waarom wil je deel uitmaken van de BETA? *</Label>
                  <Textarea
                    {...register('motivation')}
                    id="motivation"
                    placeholder="Vertel ons over je uitdagingen met leadgeneratie en wat je hoopt te bereiken met de AI Lead Engine..."
                    className={`min-h-24 ${errors.motivation ? 'border-red-500' : ''}`}
                  />
                  {errors.motivation && (
                    <p className="text-red-500 text-sm mt-1">{errors.motivation.message}</p>
                  )}
                </div>

                {/* Privacy Checkbox */}
                <div className="flex items-start space-x-3 pt-6 border-t">
                  <Checkbox
                    id="privacy"
                    checked={privacyAccepted}
                    onCheckedChange={(checked) => setValue('privacyAccepted', !!checked)}
                    className={errors.privacyAccepted ? 'border-red-500' : ''}
                  />
                  <div className="flex-1">
                    <Label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
                      Ik ga akkoord met de verwerking van mijn gegevens volgens het{' '}
                      <a href="/privacybeleid" className="text-brand-blue hover:underline" target="_blank" rel="noopener noreferrer">
                        privacybeleid
                      </a>{' '}
                      en geef toestemming om gecontacteerd te worden voor het BETA programma. *
                    </Label>
                    {errors.privacyAccepted && (
                      <p className="text-red-500 text-sm mt-1">{errors.privacyAccepted.message}</p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  variant="highlight"
                  disabled={isSubmitting}
                  className="w-full text-lg py-6"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Aanvraag verzenden...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-5 w-5" />
                      Aanvraag BETA toegang
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Trust Message */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-600">
              🔒 Je gegevens worden veilig behandeld volgens onze privacyrichtlijnen
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AILeadEngineBetaForm;
